import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import europeMapImage from "@/assets/europe-map.webp";
import CompassLogo from "@/components/CompassLogo";
import WaitlistSignup from "@/components/WaitlistSignup";

interface StartScreenProps {
  onEnter: () => void;
}

export default function StartScreen({ onEnter }: StartScreenProps) {
  const [exiting, setExiting] = useState(false);

  const handleEnter = () => {
    setExiting(true);
    setTimeout(onEnter, 800);
  };

  return (
    <AnimatePresence>
      {!exiting && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
        >
          {/* Map background */}
          <div className="absolute inset-0">
            <img
              src={europeMapImage}
              alt=""
              className="w-full h-full object-cover"
              style={{ filter: "sepia(0.3) brightness(0.3) contrast(1.1) blur(3px)" }}
              draggable={false}
            />
            <div className="absolute inset-0 bg-background/70" />
          </div>

          {/* Grain overlay */}
          <div className="absolute inset-0 grain-overlay pointer-events-none" />

          {/* Cartography grid lines */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.03]">
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={`h-${i}`} className="absolute left-0 right-0 border-t border-foreground" style={{ top: `${(i + 1) * 5}%` }} />
            ))}
            {Array.from({ length: 20 }).map((_, i) => (
              <div key={`v-${i}`} className="absolute top-0 bottom-0 border-l border-foreground" style={{ left: `${(i + 1) * 5}%` }} />
            ))}
          </div>

          {/* Fog/ambient glow */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{
                width: "70vw",
                height: "70vw",
                background: "radial-gradient(circle, hsl(var(--gold) / 0.08) 0%, transparent 70%)",
              }}
            />
          </div>

          {/* Center content */}
          <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-xl w-full pt-8 sm:pt-12">
            {/* Compass emblem */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="mb-6 sm:mb-8"
            >
              <CompassLogo size="w-20 h-20 sm:w-28 sm:h-28" className="gold-glow" />
            </motion.div>

            {/* Title, with the full descriptor in the accessible name (visually
                hidden suffix) so the page advertises what the project
                is to screen readers and search-engine snippets. */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.4 }}
              className="font-display text-2xl sm:text-3xl md:text-4xl text-gold gold-text-glow tracking-[0.12em] sm:tracking-[0.15em] mb-4 sm:mb-6"
            >
              Chronicles of Europe
              <span className="sr-only">: Interactive Historical Stories</span>
            </motion.h1>

            {/* Welcome text */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="space-y-3 mb-8 sm:mb-10"
            >
              <p className="font-display text-xs sm:text-sm text-gold-dim uppercase tracking-[0.15em] sm:tracking-[0.2em]">
                Welcome, Explorer
              </p>
              <div className="space-y-2 sm:space-y-3 text-foreground/60 font-body text-sm sm:text-base leading-relaxed">
                <p>
                  Europe's history is not only written by kings and generals.
                  <br />
                  It is lived by ordinary people.
                </p>
                <p>
                  <span className="italic text-foreground/50">Chronicles of Europe</span> lets you step into the lives
                  of people who lived through the great upheavals of history.
                </p>
                <p className="text-foreground/40 italic text-sm">
                  You cannot change history,
                  <br />
                  but you can decide how you survive it.
                </p>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.3 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleEnter}
              className="relative px-8 py-3 sm:py-3 min-h-[48px] font-display text-sm uppercase tracking-[0.2em] text-primary-foreground bg-primary/90 border border-primary rounded-sm transition-all duration-300 hover:bg-primary hover:shadow-[0_0_30px_hsl(var(--gold)/0.3)] gold-glow"
            >
              Begin Exploring Europe
            </motion.button>

            {/* Decorative border line */}
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.5, delay: 1.6 }}
              className="mt-10 sm:mt-12 w-48 h-px bg-gradient-to-r from-transparent via-gold-dim to-transparent"
            />

            {/* Waitlist — subtle, well below the primary CTA. Everyone who
                lands sees it, so it captures visitors who aren't ready to
                dive into a chronicle yet. */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.9 }}
              className="mt-8 sm:mt-10 w-full flex justify-center"
            >
              <WaitlistSignup source="startscreen" />
            </motion.div>
          </div>

          {/* Corner decorations — hidden on very small screens */}
          <div className="hidden sm:block absolute top-6 left-6 w-16 h-16 border-t border-l border-gold-dim/30" />
          <div className="hidden sm:block absolute top-6 right-6 w-16 h-16 border-t border-r border-gold-dim/30" />
          <div className="hidden sm:block absolute bottom-6 left-6 w-16 h-16 border-b border-l border-gold-dim/30" />
          <div className="hidden sm:block absolute bottom-6 right-6 w-16 h-16 border-b border-r border-gold-dim/30" />

        </motion.div>
      )}
    </AnimatePresence>
  );
}
